import { convertPromiseMapToSinglePromise } from 'actions/login_flows/loading_utils.actions';
import { createLogger } from 'helpers/logger';
import { buildDispatchPooler } from 'helpers/redux.helper';
import { getSessionRole, USER_ROLE } from 'helpers/session';
import { selectPageState } from 'selectors/page.selector';
import { SET_PAGE_STATE } from 'types/page.constants';
import { PAYLOAD } from 'types/payload.constants';

const logger = createLogger({ name: 'page.actions' });

export const setPageState =
  (newStateKeys, pageNameType) => (dispatch, getState) => {
    const finalPageNameType =
      pageNameType ?? selectPageState(getState())?.pageName;
    return { type: SET_PAGE_STATE, pageName: finalPageNameType, newStateKeys };
  };

// This function is unit tested. Please update and maintain the unit tests
// This is a critical piece of code
export const executePageLoad = c => async (dispatch, getState) => {
  let loadingActionList = c?.loadingActions ?? [];
  loadingActionList = loadingActionList.flat();
  const keysFound = [];
  loadingActionList = loadingActionList.filter(action => {
    const value = keysFound.indexOf(action.name) < 0;
    if (value) keysFound.push(action.name);
    return value;
  });
  const poolingAction = {
    type: PAYLOAD,
  };

  const { dispatch: dispatchPooler } = buildDispatchPooler({
    getState,
    poolingAction,
  });

  const ctx = {
    promiseMap: {},
    promiseFunctionMap: {},
    dependencyMapping: {},
    promiseKeys: [],
    isUserSessionActive: getSessionRole() === USER_ROLE,
    registerPromiseFunction: (key, promiseFunction, dependencies) => {
      ctx.promiseFunctionMap[key] = {
        promiseFunction,
        dependencies,
        key,
      };
    },
  };

  loadingActionList.forEach(loadingAction => {
    const promiseFunction = () => {
      return dispatchPooler(loadingAction.action(poolingAction));
    };
    const dependencies = loadingAction.dependencies ?? [];
    ctx.registerPromiseFunction(loadingAction.name, promiseFunction, [
      ...dependencies,
    ]);
  });

  ctx.promiseKeys = Object.keys(ctx.promiseFunctionMap);
  ctx.promiseKeys.forEach(k => (ctx.dependencyMapping[k] = []));

  // invert the dependencies from belonging to the one that needs it to the needed
  ctx.promiseKeys.forEach(k => {
    const promiseFunctionMetadata = ctx.promiseFunctionMap[k];

    // declare dependencies for this promise
    promiseFunctionMetadata.dependencies.forEach(dependencyKey => {
      const dependentPromiseArray = ctx.dependencyMapping[dependencyKey];
      dependentPromiseArray.push(promiseFunctionMetadata);
    });
  });

  // an array to check off that all promises have their dependencies satisfied
  let unsortedPromises = [...ctx.promiseKeys];
  // +1 to prevent circular dependency check
  let unsortedPromisesLength = unsortedPromises.length + 1;
  while (unsortedPromises.length > 0) {
    if (unsortedPromisesLength === unsortedPromises.length) {
      // for every loop, there should be atleast one promise that found a dependency
      // otherwise there is no change and it will be infinite
      throw new Error('Circular dependencies found');
    }
    unsortedPromisesLength = unsortedPromises.length;
    const keysToRemove = [];
    unsortedPromises.forEach(k => {
      const promiseFunctionMetadata = ctx.promiseFunctionMap[k];

      const areAllDependenciesCreated =
        promiseFunctionMetadata.dependencies.reduce(
          (areAllDependenciesCreated, dependencyKey) =>
            areAllDependenciesCreated && ctx.promiseMap[dependencyKey],
          true,
        );

      if (areAllDependenciesCreated) {
        const promiseFunction = promiseFunctionMetadata.promiseFunction;
        if (promiseFunctionMetadata.dependencies.length === 0) {
          ctx.promiseMap[promiseFunctionMetadata.key] = promiseFunction();
          keysToRemove.push(promiseFunctionMetadata.key);
        } else {
          let startOfPromiseChain = null;
          let currentEndOfPromiseChain = startOfPromiseChain;
          promiseFunctionMetadata.dependencies.forEach(dependency => {
            const dependentPromise = ctx.promiseMap[dependency];
            if (startOfPromiseChain === null) {
              startOfPromiseChain = dependentPromise;
              currentEndOfPromiseChain = dependentPromise;
            } else {
              startOfPromiseChain = dependentPromise;
              currentEndOfPromiseChain = currentEndOfPromiseChain.then(
                _ => dependentPromise,
              );
            }
          });

          currentEndOfPromiseChain = currentEndOfPromiseChain.then(_ => {
            return promiseFunction();
          });

          ctx.promiseMap[promiseFunctionMetadata.key] =
            currentEndOfPromiseChain;
          keysToRemove.push(promiseFunctionMetadata.key);
        }
      }
    });
    unsortedPromises = unsortedPromises.filter(
      name => keysToRemove.indexOf(name) < 0,
    );
  }

  try {
    await convertPromiseMapToSinglePromise(ctx.promiseMap);
  } catch (e) {
    logger.debugException('Unable to complete page load sequence', e);
  }
  logger.infoException('dispatching pooling action', poolingAction);
  await dispatch(poolingAction);
};
